import type { Journey } from "../contract/types";
import { runNumberOf } from "../contract/types";
import { isAbsentVehicleNumber } from "../app/format";

/**
 * Termes de recherche d'une course pour le sélecteur de véhicule des outils.
 * Trois familles de critères, saisissables indifféremment dans le même champ :
 *
 *  - **numéro de véhicule** — `vehicle.displayNumber` (« 2317 ») ;
 *  - **numéro de ligne** — le code affiché (« T3 ») **et** le n° interne, suffixe
 *    de `lineId` (`TCAR:93` → « 93 ») : au poste on dit indifféremment « le 93 »
 *    ou « le T3 » ;
 *  - **numéro de sortie / service**, chacun cherché **tel que le backend le
 *    fournit**, jamais recomposé (vérifié par Claude-2 dans `buildRunId`, 2026-09-21) :
 *      · `runId` = `{code public de ligne}-{sortie sur 2 chiffres}` (« T3-04 »,
 *        « 22-01 ») — aussi saisissable sans tiret (« T304 ») ; `runNumber` = la
 *        sortie nue (« 04 ») ;
 *      · `serviceId` = chaîne brute du PDF d'exploitation (« 12 », « S1 », « A »),
 *        **indépendante** de la ligne et de la sortie.
 *
 * Exception assumée : l'alias `{n° interne de ligne}{sortie}` (« 9301 » = sortie
 * 01 de la ligne 93). Ce n'est PAS un champ du contrat — aucune donnée ne porte
 * cette forme — mais l'exemple donné par l'utilisateur dans sa demande (« 9301
 * pour le premier service du T3 ») : la saisie du poste, pas le format des
 * données. Pour le retirer : supprimer le seul terme `internalLine + runNumber`.
 *
 * Aucune donnée nominative : uniquement des désignations de course (CLAUDE.md,
 * « Identification, sans nominatif »).
 */
export function journeySearchTerms(journey: Journey, lineCode: string): string[] {
  const terms: string[] = [];

  if (!isAbsentVehicleNumber(journey.vehicle?.displayNumber)) {
    terms.push(journey.vehicle!.displayNumber);
  }

  const internalLine = journey.lineId.split(":").pop() ?? journey.lineId;
  terms.push(lineCode, internalLine);

  const runNumber = journey.runNumber ?? runNumberOf(journey.runId);
  if (journey.runId) terms.push(journey.runId, journey.runId.replace("-", ""));
  if (runNumber) {
    terms.push(runNumber, `${internalLine}${runNumber}`); // 2ᵉ : alias de saisie, cf. en-tête
  }
  if (journey.serviceId) terms.push(journey.serviceId);

  return terms;
}
